import { XMLParser } from 'fast-xml-parser';
import getDistance from 'geolib/es/getDistance';

import {
  ITrackPointOfInterest,
  TLatLngArr,
  TLatLngBounds,
} from 'src/interfaces/TrackMap';

/** Moving-average window for elevation gain/loss filtering. */
export const ELEVATION_SMOOTH_WINDOW = 5;
/** Minimum absolute elevation change (m) before a climb/descent is counted. */
export const ELEVATION_HYSTERESIS_M = 3;

type RawPoint = {
  ele?: number | string;
  '@_lat'?: number | string;
  '@_lon'?: number | string;
  name?: string;
  desc?: string;
  sym?: string;
  type?: string;
};

type AcceptedPoint = {
  pos: TLatLngArr;
  ele: number | null;
};

type SegmentAccumulator = {
  trackPositions: TLatLngArr[];
  elevations: number[];
  elevationsNullable: (number | null)[];
  dataLabels: number[];
  segmentDistanceM: number;
};

function toArray<T>(value: T | T[] | undefined): T[] {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

function asNumber(value: unknown): number | null {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function toLatLng(point: RawPoint): TLatLngArr | null {
  const lat = asNumber(point['@_lat']);
  const lng = asNumber(point['@_lon']);
  if (lat == null || lng == null) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return [lat, lng];
}

/** Horizontal (2D) distance between consecutive track points. */
export function segmentDistanceMeters(prev: AcceptedPoint, curr: AcceptedPoint): number {
  return getDistance(
    { latitude: prev.pos[0], longitude: prev.pos[1] },
    { latitude: curr.pos[0], longitude: curr.pos[1] },
  );
}

function smoothElevations(
  elevations: (number | null)[],
  window: number = ELEVATION_SMOOTH_WINDOW,
): (number | null)[] {
  const knownCount = elevations.reduce<number>(
    (count, value) => (value != null ? count + 1 : count),
    0,
  );
  if (window <= 1 || knownCount < window) return elevations;
  const half = Math.floor(window / 2);
  return elevations.map((_, i) => {
    let sum = 0;
    let count = 0;
    for (let j = Math.max(0, i - half); j <= Math.min(elevations.length - 1, i + half); j++) {
      const value = elevations[j];
      if (value != null) {
        sum += value;
        count++;
      }
    }
    return count > 0 ? sum / count : null;
  });
}

/**
 * Elevation gain/loss with moving-average smoothing and hysteresis.
 * Missing elevations are skipped (never treated as 0).
 */
export function computeLevelDiff(
  elevations: (number | null)[],
  options?: { smoothWindow?: number; thresholdM?: number },
): { pos: number; neg: number } {
  const smoothWindow = options?.smoothWindow ?? ELEVATION_SMOOTH_WINDOW;
  const thresholdM = options?.thresholdM ?? ELEVATION_HYSTERESIS_M;
  const smoothed = smoothElevations(elevations, smoothWindow);

  let pos = 0;
  let neg = 0;
  let pending = 0;
  let last: number | null = null;

  for (const value of smoothed) {
    if (value == null) continue;
    if (last == null) {
      last = value;
      continue;
    }
    const delta = value - last;
    last = value;
    if (delta === 0) continue;

    if (pending === 0 || Math.sign(delta) === Math.sign(pending)) {
      pending += delta;
    } else {
      if (Math.abs(pending) >= thresholdM) {
        if (pending > 0) pos += pending;
        else neg += Math.abs(pending);
      }
      pending = delta;
    }
  }

  if (Math.abs(pending) >= thresholdM) {
    if (pending > 0) pos += pending;
    else neg += Math.abs(pending);
  }

  return {
    pos: parseFloat(pos.toFixed(2)),
    neg: parseFloat(neg.toFixed(2)),
  };
}

function processSegment(
  rawPoints: RawPoint[],
  cumulativeDistanceM: number,
): { result: SegmentAccumulator; cumulativeDistanceM: number } {
  const result: SegmentAccumulator = {
    trackPositions: [],
    elevations: [],
    elevationsNullable: [],
    dataLabels: [],
    segmentDistanceM: 0,
  };

  let lastAccepted: AcceptedPoint | null = null;
  let dist = cumulativeDistanceM;

  for (const point of rawPoints) {
    const pos = toLatLng(point);
    if (!pos) continue;

    const ele = asNumber(point.ele);

    if (lastAccepted) {
      const d = segmentDistanceMeters(lastAccepted, { pos, ele });
      dist += d;
      result.segmentDistanceM += d;
    }

    result.trackPositions.push(pos);
    result.elevationsNullable.push(ele);
    result.elevations.push(parseFloat((ele ?? 0).toFixed(2)));
    result.dataLabels.push(parseFloat((dist / 1000).toFixed(2)));
    lastAccepted = { pos, ele };
  }

  return { result, cumulativeDistanceM: dist };
}

type RawSegment = { trkpt?: RawPoint | RawPoint[] };
type RawTrack = { trkseg?: RawSegment | RawSegment[] };
type RawRoute = { rtept?: RawPoint | RawPoint[] };

function collectSegments(gpx: { trk?: RawTrack | RawTrack[]; rte?: RawRoute | RawRoute[] }): RawPoint[][] {
  const tracks = toArray(gpx.trk);
  const trackSegments = tracks.flatMap(track => toArray(track?.trkseg));
  const trackPoints = trackSegments.flatMap(segment => toArray<RawPoint>(segment?.trkpt));

  if (trackPoints.length > 0) {
    return trackSegments.map(segment => toArray<RawPoint>(segment?.trkpt)).filter(seg => seg.length > 0);
  }

  const routes = toArray(gpx.rte);
  return routes.map(route => toArray<RawPoint>(route?.rtept)).filter(seg => seg.length > 0);
}

export const parseGPXFile = (gpxFile: string) => {
  const parser = new XMLParser({
    ignoreAttributes: false,
  });
  const parsed = parser.parse(gpxFile);

  const gpx = parsed.gpx || {};
  const metadata = gpx.metadata || {};
  const rawBounds = metadata.bounds || {};
  const segments = collectSegments(gpx);

  if (!segments.length) {
    throw new Error('GPX file does not contain track points.');
  }

  const checkPoints: TLatLngArr[] = [];
  const pointsOfInterest: ITrackPointOfInterest[] = [];
  const trkPos: TLatLngArr[] = [];
  const lbls: number[] = [];
  const elev: number[] = [];
  const elevNullable: (number | null)[] = [];
  let dist = 0;

  for (const segment of segments) {
    const { result, cumulativeDistanceM } = processSegment(segment, dist);
    trkPos.push(...result.trackPositions);
    elev.push(...result.elevations);
    elevNullable.push(...result.elevationsNullable);
    lbls.push(...result.dataLabels);
    dist = cumulativeDistanceM;
  }

  if (!trkPos.length || trkPos.length !== elev.length || trkPos.length !== lbls.length) {
    throw new Error('GPX parsing produced inconsistent track arrays.');
  }

  const levelDiff = computeLevelDiff(elevNullable);

  toArray<RawPoint>(gpx.wpt).forEach(point => {
    const pos = toLatLng(point);
    if (!pos) return;
    checkPoints.push(pos);
    pointsOfInterest.push({
      lat: pos[0],
      lng: pos[1],
      name: point.name,
      description: point.desc,
      symbol: point.sym,
      type: point.type,
    });
  });

  const boundsFromMetadata: TLatLngBounds = {
    southWest: [asNumber(rawBounds['@_minlat']) ?? NaN, asNumber(rawBounds['@_minlon']) ?? NaN],
    northEast: [asNumber(rawBounds['@_maxlat']) ?? NaN, asNumber(rawBounds['@_maxlon']) ?? NaN],
  };

  const seed = trkPos[0];
  if (!seed) {
    throw new Error('GPX parsing failed to produce coordinates.');
  }
  const computedBounds: TLatLngBounds = trkPos.reduce(
    (acc, [lat, lng]) => ({
      southWest: [Math.min(acc.southWest[0], lat), Math.min(acc.southWest[1], lng)],
      northEast: [Math.max(acc.northEast[0], lat), Math.max(acc.northEast[1], lng)],
    }),
    {
      southWest: [seed[0], seed[1]],
      northEast: [seed[0], seed[1]],
    } as TLatLngBounds,
  );

  const hasValidMetadataBounds =
    Number.isFinite(boundsFromMetadata.southWest[0]) &&
    Number.isFinite(boundsFromMetadata.southWest[1]) &&
    Number.isFinite(boundsFromMetadata.northEast[0]) &&
    Number.isFinite(boundsFromMetadata.northEast[1]);

  const latLngBounds = hasValidMetadataBounds ? boundsFromMetadata : computedBounds;

  return {
    bounds: latLngBounds,
    checkPoints,
    pointsOfInterest,
    trackPositions: trkPos,
    dataLabels: lbls,
    elevations: elev,
    levelDiff,
    totalDistance: parseFloat((dist / 1000).toFixed(2)),
  };
};
